/**
 * Wall-photo + design-sketch overlay (Spec 007 FR-OVERLAY).
 *
 * When the customer has uploaded at least one wall photo AND recorded
 * a scale anchor with `value_inches`, the workspace can drop the
 * design SVG on top of the photo at horizontal scale derived from
 * the anchor's known length. V1 uses a coarse heuristic — the
 * anchor's relationship to the photo's horizontal extent depends on
 * the anchor kind: a `letter_page` is a small reference object inside
 * the photo (~1/3 of the photo width); a `known_dimensions` or
 * `wall_height` reading typically describes the wall itself, so the
 * anchor occupies roughly the full photo width. The customer can
 * fine-tune with the scale slider when the heuristic mismatches.
 *
 * V1 limitations explicitly called out in the disclaimer text:
 * - Horizontal scale only; no perspective correction.
 * - Default ratios are approximate; slider lets the customer adjust.
 * - Future spec adds tap-to-anchor for precise scale.
 */

import { useRef, useState } from "react";

import type {
  AnchorKind,
  Design,
  DesignElement,
  Photo,
  ScaleAnchor,
} from "@starz/shared/api/types.gen";

interface Props {
  photo: Photo;
  anchor: ScaleAnchor;
  design: Design;
}

const KIND_FILL: Record<DesignElement["kind"], string> = {
  rect: "rgba(250, 250, 247, 0.55)",
  drawer: "rgba(245, 239, 230, 0.6)",
  tv_opening: "rgba(31, 41, 55, 0.55)",
  shelf: "transparent",
  door: "rgba(251, 249, 243, 0.55)",
  cabinet: "rgba(251, 249, 243, 0.55)",
};

const KIND_STROKE = "#1E3D58";
const SHELF_STROKE = "#8B6D2C";

/** Default fraction of the photo's horizontal extent that the anchor
 * is assumed to span. `letter_page` is a small reference object held
 * up to the wall; `known_dimensions` and `wall_height` are typically
 * whole-wall measurements so the anchor ≈ the visible wall width. */
const ANCHOR_TO_PHOTO_RATIO_BY_KIND: Record<AnchorKind, number> = {
  letter_page: 1 / 3,
  known_dimensions: 1.0,
  wall_height: 1.0,
  // RoomPlan + AR-measure anchors aren't tied to a photo, so this
  // overlay is never drawn for them; the entries exist only to satisfy
  // the exhaustive Record type.
  room_plan: 1.0,
  ar_linear: 1.0,
  ar_surface: 1.0,
};

function _photoUrl(photo: Photo): string | null {
  return photo.original_url ?? photo.thumbnail_url ?? null;
}

/** Drag state for the ephemeral reposition gesture. `dx` / `dy` are
 * pixel offsets from the centered baseline; reset on toggle-off and
 * on the explicit "Recenter" link. */
interface DragState {
  pointerId: number;
  startX: number;
  startY: number;
  baseDx: number;
  baseDy: number;
}

export function WallOverlayToggle({ photo, anchor, design }: Props) {
  const [show, setShow] = useState(false);
  const [offset, setOffset] = useState<{ dx: number; dy: number }>({
    dx: 0,
    dy: 0,
  });
  const [scale, setScale] = useState(1);
  const dragRef = useRef<DragState | null>(null);
  const photoSrc = _photoUrl(photo);
  if (photoSrc == null || anchor.value_inches == null || anchor.value_inches <= 0) {
    return null;
  }

  // Photo width represents `anchor_inches / ratio` inches. The overlay's
  // design occupies (outer_w / wall_inches) of the photo's display
  // width, then multiplied by the customer's `scale` adjustment.
  const ratio = ANCHOR_TO_PHOTO_RATIO_BY_KIND[anchor.kind];
  const wallInches = anchor.value_inches / ratio;
  const baseFraction = design.outer_w / wallInches;
  const widthFraction = Math.min(1, Math.max(0.05, baseFraction * scale));
  const overlayWidthPct = widthFraction * 100;
  // Aspect ratio for the overlay's container — preserves design's
  // outer_w × outer_h shape independently of the photo's aspect.
  const overlayAspect = `${design.outer_w} / ${design.outer_h}`;

  function onPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      baseDx: offset.dx,
      baseDy: offset.dy,
    };
  }

  function onPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    setOffset({
      dx: drag.baseDx + (e.clientX - drag.startX),
      dy: drag.baseDy + (e.clientY - drag.startY),
    });
  }

  function endDrag(e: React.PointerEvent<HTMLDivElement>) {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    dragRef.current = null;
  }

  function onToggle(next: boolean) {
    setShow(next);
    if (!next) {
      setOffset({ dx: 0, dy: 0 });
      setScale(1);
    }
  }

  function reset() {
    setOffset({ dx: 0, dy: 0 });
    setScale(1);
  }

  const isAdjusted = offset.dx !== 0 || offset.dy !== 0 || scale !== 1;

  return (
    <section
      aria-label="Apply design to wall photo"
      className="space-y-3 rounded-xl border border-starz-gold/20 bg-white p-4 shadow-sm"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
          Wall preview
        </p>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-starz-navy">
          <input
            type="checkbox"
            checked={show}
            onChange={(e) => onToggle(e.target.checked)}
            className="h-4 w-4 rounded border-starz-gold/40"
          />
          Apply design to this wall
        </label>
      </div>
      <div className="relative w-full overflow-hidden rounded-lg border border-starz-gold/15 bg-starz-cream/30">
        <img
          src={photoSrc}
          alt="Wall photo"
          className="block w-full select-none"
          loading="lazy"
          draggable={false}
        />
        {show && (
          <div
            role="img"
            aria-label="Design overlay (drag to reposition)"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            className="absolute left-1/2 top-1/2 cursor-grab touch-none active:cursor-grabbing"
            style={{
              width: `${overlayWidthPct}%`,
              aspectRatio: overlayAspect,
              transform: `translate(calc(-50% + ${offset.dx}px), calc(-50% + ${offset.dy}px))`,
            }}
          >
            <svg
              viewBox={`0 0 ${design.outer_w} ${design.outer_h}`}
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid meet"
              className="block h-full w-full"
            >
              <rect
                x={0}
                y={0}
                width={design.outer_w}
                height={design.outer_h}
                fill="rgba(255,255,255,0.18)"
                stroke={KIND_STROKE}
                strokeWidth={Math.max(0.4, design.outer_w * 0.005)}
              />
              {design.elements.map((el, i) => (
                <g key={i}>
                  <rect
                    x={el.x}
                    y={el.y}
                    width={el.w}
                    height={el.h}
                    fill={KIND_FILL[el.kind]}
                    stroke={el.kind === "shelf" ? SHELF_STROKE : KIND_STROKE}
                    strokeWidth={Math.max(0.3, design.outer_w * 0.004)}
                  />
                  {el.kind === "shelf" && (
                    <line
                      x1={el.x}
                      y1={el.y + el.h / 2}
                      x2={el.x + el.w}
                      y2={el.y + el.h / 2}
                      stroke={SHELF_STROKE}
                      strokeWidth={Math.max(0.3, design.outer_w * 0.004)}
                    />
                  )}
                </g>
              ))}
            </svg>
          </div>
        )}
      </div>
      {show && (
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <label
              htmlFor="overlay-scale"
              className="text-xs font-semibold uppercase tracking-widest text-starz-charcoal"
            >
              Scale
            </label>
            <input
              id="overlay-scale"
              type="range"
              min={0.5}
              max={2}
              step={0.05}
              value={scale}
              onChange={(e) => setScale(Number(e.target.value))}
              className="flex-1 accent-starz-gold"
              aria-label="Adjust overlay scale"
            />
            <span className="w-10 text-right font-mono text-xs text-starz-charcoal">
              {Math.round(scale * 100)}%
            </span>
          </div>
          <div className="flex items-center justify-between gap-3">
            <p className="text-[11px] italic text-warm-gray">
              Drag to reposition; slide to resize. Approximate placement based on the anchor "
              {anchor.value_text}". Final design subject to contractor confirmation.
            </p>
            {isAdjusted && (
              <button
                type="button"
                onClick={reset}
                className="shrink-0 text-xs font-semibold uppercase tracking-widest text-starz-gold hover:underline"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
